import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  errorCodeSelector,
  loadingSelector,
  noMoreSelector,
  notificationsSelector,
  pageSelector,
  unreadLoadedSelector,
  unreadLoadingSelector,
  unreadNotificationsSelector,
} from './store/selector';
import {
  getNotificationFailureApiAction,
  getNotificationInitApiAction,
  getNotificationSuccessApiAction,
  getUnreadNotificationFailureApiAction,
  getUnreadNotificationInitApiAction,
  getUnreadNotificationSuccessApiAction,
  increasePageAction,
  notificationClearAction,
  notificationListResetAction,
  postReadAllFailuireApiAction,
  postReadAllInitApiAction,
  postReadAllSuccessApiAction,
  postReadFailuireApiAction,
  postReadInitApiAction,
  postReadSuccessApiAction,
  resetPageAction
} from './store/reducer';
import { notificationApiService } from './services/api';

export function useNotificationCore() {
  const notifications = useSelector(notificationsSelector);
  const unreadNotifications = useSelector(unreadNotificationsSelector);
  const page = useSelector(pageSelector);
  const noMore = useSelector(noMoreSelector);
  const errorCode = useSelector(errorCodeSelector);
  const loading = useSelector(loadingSelector);
  const unreadLoading = useSelector(unreadLoadingSelector);
  const unreadLoaded = useSelector(unreadLoadedSelector);
  const dispatch = useDispatch();

  const getNotifications = useCallback(
    async (
      page: number,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(getNotificationInitApiAction());
        const response = await notificationApiService.getAllNotifications(page);
        dispatch(getNotificationSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          getNotificationFailureApiAction({ errorCode: error?.request?.status })
        );
      }
    },
    [dispatch]
  );

  const getUnreadNotifications = useCallback(
    async () => {
      try {
        dispatch(getUnreadNotificationInitApiAction());
        const response = await notificationApiService.getUnreadNotifications();
        dispatch(getUnreadNotificationSuccessApiAction(response));
      } catch (error: any) {
        console.error(error);
        dispatch(
          getUnreadNotificationFailureApiAction({ errorCode: error?.request?.status })
        );
      }
    },
    [dispatch]
  );

  const postReadNotification = useCallback(
    async (id: string, type: string, onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(postReadInitApiAction());
        await notificationApiService.postReadNotification(id, type);
        dispatch(postReadSuccessApiAction({ id, type }));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(postReadFailuireApiAction());
      }
    },
    [dispatch]
  );

  const postReadAllNotifications = useCallback(
    async (onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(postReadAllInitApiAction());
        await notificationApiService.postReadAllNotification();
        dispatch(postReadAllSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(postReadAllFailuireApiAction());
      }
    },
    [dispatch]
  );

  const clearNotification = useCallback(() => {
    dispatch(notificationClearAction());
  }, [dispatch]);

  const resetNotificationList = useCallback(() => {
    dispatch(notificationListResetAction());
  }, [dispatch]);

  const resetPage = useCallback(() => {
    dispatch(resetPageAction());
  }, [dispatch]);

  const increasePage = useCallback(() => {
    dispatch(increasePageAction());
  }, [dispatch]);

  return {
    notifications,
    unreadNotifications,
    page,
    noMore,
    errorCode,
    loading,
    unreadLoading,
    unreadLoaded,
    getNotifications,
    getUnreadNotifications,
    postReadAllNotifications,
    postReadNotification,
    clearNotification,
    resetNotificationList,
    resetPage,
    increasePage,
  };
}

export * from './store/selector';
